import { AuthError, AuthErrorMessages } from "@/lib/errors";
import { createClient } from "@/utils/supabase/client";
import { z } from "zod";

const resetPasswordSchema = z.object({
  password: z
    .string()
    .min(6, {
      message: "Password must be at least 6 characters",
    }),
});

type ResetPasswordResult =
  | { success: true }
  | { success: false; error: string };

export async function resetPassword(
  formData: FormData,
): Promise<ResetPasswordResult> {
  const supabase = await createClient();

  // Check if user is authenticated
  const { data } = await supabase.auth.getClaims();

  if (!data?.claims) {
    return {
      success: false,
      error: AuthErrorMessages[AuthError.NO_USER_SESSION],
    };
  }

  const password = formData.get("password");
  const validatedFields = resetPasswordSchema.safeParse({
    password,
  });

  if (!validatedFields.success) {
    return {
      success: false,
      error: AuthErrorMessages[AuthError.INVALID_PASSWORD],
    };
  }

  const { error } = await supabase.auth.updateUser({
    password: validatedFields.data.password,
  });

  if (error) {
    return {
      success: false,
      error: AuthErrorMessages[
        AuthError.PASSWORD_UPDATE_FAILED
      ] ?? error.message,
    };
  }

  await supabase.auth.refreshSession();

  return { success: true };
}
